import { useEffect, useState } from 'preact/hooks';
import { api } from '../services/api.js';

/**
 * 406 (#1369): Malaysian banks are offline Friday 00:00–01:30, so the platform refuses customer payments and bookings
 * then. The SERVER decides (`GET /payment-pause`); this hook only mirrors it so a page can show the notice instead of
 * a button that would be refused, and swap back at 01:30 without a reload. The next edge is timed from the server's
 * own `now`, never the device clock, which may be wrong. A failed read reports "not paused": the refusal stays
 * authoritative.
 */
export interface PaymentPauseAnswer {
  paused: boolean;
  ends_at: string | null;
  next_starts_at: string;
  now: string;
}

/** A timer cannot wait longer than this (2^31 − 1 ms); a later edge is re-armed on the next read. */
const MAX_TIMER_MS = 2_147_483_647;
/** A little after the edge, so the server's clock has passed it too. */
const SETTLE_MS = 2_000;

export function usePaymentPause(): { paused: boolean } {
  const [answer, setAnswer] = useState<PaymentPauseAnswer | null>(null);
  const [reads, setReads] = useState(0);

  // `reads` is a re-read trigger, not a value used in the body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see prose above
  useEffect(() => {
    let live = true;
    // Started inside a promise, so a read that throws at once is a failed read like any other.
    Promise.resolve()
      .then(() => api.get<PaymentPauseAnswer>('/payment-pause'))
      .then((a) => {
        if (live) setAnswer(a && typeof a.paused === 'boolean' ? a : null);
      })
      .catch(() => {
        if (live) setAnswer(null);
      });
    return () => {
      live = false;
    };
  }, [reads]);

  useEffect(() => {
    if (!answer) return;
    const edge = answer.paused ? answer.ends_at : answer.next_starts_at;
    if (!edge) return;
    const wait = new Date(edge).getTime() - new Date(answer.now).getTime() + SETTLE_MS;
    // Ask again just past the edge. A read that fails then reports "not paused" (above), so the buttons come back at
    // 01:30 even offline; the server's refusal still covers the rare wrong guess.
    const timer = setTimeout(
      () => setReads((n) => n + 1),
      Math.min(Math.max(wait, 0), MAX_TIMER_MS),
    );
    return () => clearTimeout(timer);
  }, [answer]);

  return { paused: answer?.paused === true };
}
